// @ts-check
// Limite diário da Meta e retenções de um número de WhatsApp (contrato 09 de campanhas de WhatsApp, seção 3;
// spec/whatsapp.md 18.6 e K38): nível do portfólio, usados nas últimas 24 h, quanto as campanhas podem usar e, quando
// a Meta segura os envios em massa do número ou da conta, o aviso com "Liberar os envios" depois do prazo mínimo (só
// WORKSPACE, com confirmação). Mostrado em Configurações > Canais; a lista das campanhas (2B) pode reaproveitar.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { getWhatsappMessagingLimit, releaseWhatsappSendingHold } from '../../data/integrations.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { describeMessagingLimit } from '../../lib/whatsapp-templates.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').WhatsappMessagingLimit} WhatsappMessagingLimit
 * @typedef {import('../../data/integrations.js').WhatsappSendingHold} WhatsappSendingHold
 */

/**
 * Aviso de uma retenção da Meta, com a liberação depois do prazo mínimo.
 * @param {{ hold: WhatsappSendingHold, canRelease: boolean, onRelease: (hold: WhatsappSendingHold) => void }} props
 */
function SendingHoldNotice({ hold, canRelease, onRelease }) {
  const isAvailable = Date.parse(hold.minimumUntil) <= Date.now();
  return html`<div class="wa-notice" data-tone="danger" role="alert">
    <${Icon} name="IconAlertTriangle" size=${16} />
    <div class="wa-notice-body">
      <p><strong>${t(`settings.channels.whatsapp.sendingHold.${hold.scope}`)}</strong></p>
      ${hold.code ? html`<p>${t('settings.channels.whatsapp.sendingHold.code', { code: hold.code })}</p>` : null}
      <p>${t('settings.channels.whatsapp.sendingHold.explain')}</p>
      ${!isAvailable ? html`<p>${t('settings.channels.whatsapp.sendingHold.availableAt', { date: formatDateTime(hold.minimumUntil) })}</p>` : null}
      ${canRelease
        ? (isAvailable ? html`<${Button} title=${t('settings.channels.whatsapp.sendingHold.release')} icon="IconPlayerPlay" variant="secondary" size="small" onClick=${() => onRelease(hold)} />` : null)
        : html`<p>${t('settings.channels.whatsapp.sendingHold.adminOnly')}</p>`}
    </div>
  </div>`;
}

/**
 * @param {{ channelId: string, hasWaba: boolean }} props
 */
export function WhatsappMessagingLimit({ channelId, hasWaba }) {
  const canSee = hasCurrentPermissionFlag('WORKSPACE') || hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS');
  const [data, setData] = useState(/** @type {WhatsappMessagingLimit | null} */ (null));
  const [hasFailed, setHasFailed] = useState(false);
  const [pendingRelease, setPendingRelease] = useState(/** @type {WhatsappSendingHold | null} */ (null));
  const [isReleasing, setIsReleasing] = useState(false);

  const load = () => {
    setHasFailed(false);
    getWhatsappMessagingLimit(channelId).then(setData).catch(() => setHasFailed(true));
  };
  useEffect(() => {
    if (canSee && hasWaba) load();
  }, [channelId, hasWaba]);

  if (!canSee) return null;
  const title = html`<p class="wa-limit-title"><${Icon} name="IconProgress" size=${14} />${t('settings.channels.whatsapp.messagingLimit.title')}</p>`;
  if (!hasWaba) return html`<div class="wa-limit">${title}<p class="wa-limit-help">${t('settings.channels.whatsapp.messagingLimit.noWaba')}</p></div>`;
  if (hasFailed) {
    return html`<div class="wa-limit">${title}
      <p class="wa-limit-retry" role="alert"><span>${t('settings.channels.whatsapp.messagingLimit.loadFailed')}</span>
        <${Button} title=${t('settings.channels.whatsapp.messagingLimit.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></p>
    </div>`;
  }
  if (!data) return html`<div class="wa-limit" aria-busy="true">${title}<${Skeleton} height=${16} radius=${4} /></div>`;

  const { portfolio, holds } = data;
  const { isUnlimited, usedShare, isFull } = describeMessagingLimit(portfolio);
  const release = async () => {
    if (!pendingRelease) return;
    setIsReleasing(true);
    try {
      await releaseWhatsappSendingHold(channelId, pendingRelease.scope);
      showSuccessSnackbar(t('settings.channels.whatsapp.sendingHold.released'));
      setPendingRelease(null);
      load();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsReleasing(false);
    }
  };

  return html`<div class="wa-limit" data-testid="whatsapp-messaging-limit">
    <div class="wa-limit-header">
      ${title}
      <span class="wa-limit-tier">${isUnlimited
        ? t('settings.channels.whatsapp.messagingLimit.unlimited')
        : t('settings.channels.whatsapp.messagingLimit.tier', { limit: formatNumber(portfolio.limit ?? 0) })}</span>
    </div>
    ${!isUnlimited
      ? html`<div class="wa-limit-meter" data-full=${isFull ? 'true' : undefined} role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow=${usedShare}
          aria-label=${t('settings.channels.whatsapp.messagingLimit.used', { used: formatNumber(portfolio.usedLast24h) })}>
          <div class="wa-limit-meter-fill" style=${`width: ${usedShare}%`}></div>
        </div>`
      : null}
    <ul class="wa-limit-facts">
      <li>${t('settings.channels.whatsapp.messagingLimit.used', { used: formatNumber(portfolio.usedLast24h) })}</li>
      ${portfolio.cap !== null ? html`<li>${t('settings.channels.whatsapp.messagingLimit.campaignCap', { cap: formatNumber(portfolio.cap) })}</li>` : null}
      <li>${portfolio.isPortfolioConfirmed && portfolio.labelSuffix
        ? t('settings.channels.whatsapp.messagingLimit.portfolio', { suffix: portfolio.labelSuffix })
        : t('settings.channels.whatsapp.messagingLimit.portfolioPending')}</li>
    </ul>
    ${!portfolio.isLimitConfirmed ? html`<p class="wa-notice" data-tone="info"><${Icon} name="IconInfoCircle" size=${16} /><span>${t('settings.channels.whatsapp.messagingLimit.notConfirmed')}</span></p>` : null}
    ${isFull && portfolio.nextSlotsAt ? html`<p class="wa-notice"><${Icon} name="IconClock" size=${16} /><span>${t('settings.channels.whatsapp.messagingLimit.fullUntil', { time: formatDateTime(portfolio.nextSlotsAt) })}</span></p>` : null}
    ${[holds.phone, holds.portfolio].filter(Boolean).map((hold) => html`<${SendingHoldNotice} key=${hold?.scope} hold=${hold} canRelease=${data.canReleaseHolds} onRelease=${setPendingRelease} />`)}
    <p class="wa-limit-help">${t('settings.channels.whatsapp.messagingLimit.help')}</p>
    ${pendingRelease
      ? html`<${Modal} title=${t('settings.channels.whatsapp.sendingHold.releaseTitle')} subtitle=${t('settings.channels.whatsapp.sendingHold.releaseText')}
          confirmLabel=${t('settings.channels.whatsapp.sendingHold.release')} isLoading=${isReleasing} onClose=${() => setPendingRelease(null)} onConfirm=${release} />`
      : null}
  </div>`;
}
